import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Phone, Home, Droplets, Flame, Gauge, ShieldCheck, ArrowRight } from 'lucide-react';
import { trackThankYouPageView } from '../lib/analytics';

const PHONE = "403-615-8770";

const suggestedServices = [
  { name: 'Plumbing', path: '/plumbing', icon: Droplets, desc: 'Repairs, installs & water heaters' },
  { name: 'Heating', path: '/heating', icon: Flame, desc: 'Furnace, boilers & in-floor heat' },
  { name: 'Gas Fitting', path: '/gas-fitting', icon: Gauge, desc: 'BBQ, fireplaces, appliances' },
  { name: 'Backflow Testing', path: '/plumbing/backflow-testing-calgary', icon: ShieldCheck, desc: 'City of Calgary certified' },
];

export const ThankYouPage = () => {
  useEffect(() => {
    trackThankYouPageView();
  }, []);

  return (
    <main data-testid="thank-you-page" className="page-transition min-h-screen section-tint">
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Success Icon */}
            <div className="inline-flex p-5 mb-8 bg-green-100 rounded-full">
              <CheckCircle className="w-16 h-16 text-green-600" strokeWidth={1.5} />
            </div>

            {/* Headline */}
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mb-6">
              Thank You – Your Request Has Been Received
            </h1>

            {/* Message */}
            <p className="text-slate-600 text-lg mb-8 max-w-xl mx-auto">
              We've received your service request and will contact you shortly.
              For 24 hour emergency service, please call us directly.
            </p>

            {/* Phone Number - Prominent */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 max-w-md mx-auto mb-8">
              <p className="text-slate-500 text-sm mb-2">For immediate assistance:</p>
              <a 
                href={`tel:${PHONE}`}
                data-testid="thank-you-phone"
                className="text-[#0F172A] text-3xl sm:text-4xl font-black hover:text-[#0EA5E9] transition-colors"
              >
                {PHONE}
              </a>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${PHONE}`}
                data-testid="call-now-btn"
                className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <Link
                to="/"
                data-testid="return-home-btn"
                className="bg-[#0F172A] hover:bg-[#075985] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Return to Homepage
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Suggested Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-2">
                Explore Our Other Services
              </h2>
              <p className="text-slate-600">
                While you wait, learn more about what DNB can do for you.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {suggestedServices.map((service, index) => (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <Link
                    to={service.path}
                    data-testid={`service-link-${service.name.toLowerCase().replace(' ', '-')}`}
                    className="block p-6 section-tint rounded-xl border border-slate-200 hover:border-[#0F172A]/30 hover:shadow-lg transition-all group text-center"
                  >
                    <div className="inline-flex p-3 bg-[#0F172A]/10 rounded-xl mb-4 group-hover:bg-[#0F172A]/20 transition-colors">
                      <service.icon className="w-6 h-6 text-[#0F172A]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-1 group-hover:text-[#0F172A] transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-slate-500 text-sm">{service.desc}</p>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* View All Services Link */}
            <div className="text-center mt-8">
              <Link
                to="/services/plumbing"
                className="inline-flex items-center gap-2 text-[#0F172A] font-semibold hover:gap-3 transition-all"
              >
                View All Services
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Message */}
      <section className="py-12 section-tint">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600">
            <span className="font-semibold text-[#0F172A]">DNB</span> – Calgary's trusted local contractor for plumbing, heating, and gas fitting.
          </p>
          <p className="text-[#0F172A] font-medium mt-2">
            Residential &middot; Light Commercial &middot; Service &amp; Renovation
          </p>
        </div>
      </section>
    </main>
  );
};

export default ThankYouPage;
